import { type ComponentType } from "react";
import NrrChart from "@/components/cheatsheet/NrrChart";
import CacChart from "@/components/cheatsheet/CacChart";
import { CacPaybackChart, LtvCacRatioChart } from "@/components/cheatsheet/unitEconomicsCharts";
import {
  ActivationBenchmarksChart,
  ActivationMixChart,
  BounceRateBenchmarksChart,
  TrafficSourcesChart,
  ActivationRetentionSplitChart,
  ChurnBenchmarksChart,
  ChurnByTenureChart,
  ChurnCompoundingChart,
  NrrBenchmarksChart,
  NrrGrrCompositionChart,
  NrrWhaleTestChart,
  StripeMrrBridgeChart,
  TrialToPaidBenchmarksChart,
} from "@/components/cheatsheet/benchmarkCharts";
import {
  AcvChart,
  ArrPerEmployeeChart,
  BurnMultipleChart,
  CmrrChart,
  DauMauChart,
  DealStagesChart,
  GrossMarginChart,
  MagicNumberChart,
  RenewalRateChart,
} from "@/components/cheatsheet/glossaryCharts";
import LtvCacChart from "@/components/cheatsheet/LtvCacChart";
import ExpansionMrrChart from "@/components/cheatsheet/ExpansionMrrChart";
import ContractionMrrChart from "@/components/cheatsheet/ContractionMrrChart";
import NewMrrChart from "@/components/cheatsheet/NewMrrChart";
import ChurnMrrChart from "@/components/cheatsheet/ChurnMrrChart";
import ReactivationMrrChart from "@/components/cheatsheet/ReactivationMrrChart";
import MrrMovementsChart from "@/components/cheatsheet/MrrMovementsChart";
import GrrChart from "@/components/cheatsheet/GrrChart";
import MrrChart from "@/components/cheatsheet/MrrChart";
import NetNewRevenueChart from "@/components/cheatsheet/NetNewRevenueChart";
import MrrGrowthRateChart from "@/components/cheatsheet/MrrGrowthRateChart";
import ArrChart from "@/components/cheatsheet/ArrChart";
import PayingAccountsChart from "@/components/cheatsheet/PayingAccountsChart";
import PayingCustomerMovementsChart from "@/components/cheatsheet/PayingCustomerMovementsChart";
import {
  AverageChurnAmountChart,
  AverageContractionAmountChart,
  AverageExpansionAmountChart,
  AverageReactivationAmountChart,
  AverageSalesPriceChart,
} from "@/components/cheatsheet/averageAmountCharts";
import {
  AverageAccountLifespanChart,
  AverageCompanyLifespanChart,
  CustomersRetentionRateChart,
  MedianTimeToReturnChart,
  RealizedClvChart,
  WinBackRateChart,
} from "@/components/cheatsheet/retentionDepthCharts";
import {
  ActivationRateChart,
  ConversionRateChart,
  ExpansionRateChart,
  SignupsChart,
  TimeToActivatedChart,
  TimeToEngagedChart,
  TrialsStartedChart,
  TrialToPaidRateChart,
} from "@/components/cheatsheet/activationConversionCharts";
import {
  ExpansionToContractionCountRatioChart,
  ExpansionVsContractionRatioChart,
  GrossChurnCoverageChart,
  GrossToNetSpreadChart,
  GrowthEfficiencyIndexChart,
  GrowthQuickRatioChart,
  RuleOf40Chart,
} from "@/components/cheatsheet/growthQualityCharts";
import {
  ActiveAccountsChart,
  ArpaChart,
  ArpuChart,
  ChurnedPayingAccountsChart,
  ClvChart,
  EngagedAccountsChart,
  NewPayingAccountsChart,
} from "@/components/cheatsheet/customerCharts";
import {
  AverageActiveUsersChart,
  ChurnAccelerationChart,
  NetRevenueChurnChart,
  RevenueChurnRateChart,
  ContractionBeforeCancelChart,
  CustomerChurnRateChart,
  LogoVsDollarChurnGapChart,
  PayingDormantChart,
  SingleUserCountChart,
  SingleUserMrrShareChart,
  ZombieRevenueChart,
} from "@/components/cheatsheet/churnUsageCharts";
import {
  AnnualizedGrowthRateChart,
  AnnualShareChart,
  ContractionVelocityChart,
  DurableMrrChart,
  ExpansionSaturationChart,
  ExpansionVelocityChart,
  GrowthAccelerationChart,
  GrowthVolatilityChart,
  MonthsToDoubleChart,
  ReactivationShareChart,
  SmoothedMrrGrowthChart,
} from "@/components/cheatsheet/growthTrendsCharts";
import {
  AvgNewAccountsDiscountChart,
  AvgNewCompaniesDiscountChart,
  DiscountedFirstPaymentRateChart,
  DiscountedNewAccountsChart,
  DiscountedNewCompaniesChart,
  DiscountValueChart,
  DisputeRateChart,
  MrrDiscountAmountChart,
  RefundRateChart,
} from "@/components/cheatsheet/discountRiskCharts";
import { NrrCohortChart } from "@/components/cheatsheet/cohortCharts";

/**
 * Native metric charts keyed by library post slug.
 *
 * When a post's slug is present here, LibraryPost renders the chart in the hero
 * slot instead of the `image` field or the gradient placeholder. Native charts
 * are the preferred approach over screenshots: they are theme-aware (light/dark
 * via CSS variables), crisp at any resolution, accessible and crawlable (the
 * ChartCard caption carries the meaning), and never drift from the product.
 *
 * To add a metric visual:
 *   1. Add coherent numbers to src/components/cheatsheet/exampleData.ts.
 *   2. Build a chart in src/components/cheatsheet/<Metric>Chart.tsx, following an
 *      existing one (e.g. ExpansionMrrChart, RetentionChart) — reuse chartTheme
 *      and wrap it in ChartCard.
 *   3. Map the post slug to the chart component here.
 */
export const metricCharts: Record<string, ComponentType> = {
  nrr: NrrChart,
  // NDR is the same metric as NRR (US term) — same chart on both pages.
  "net-dollar-retention": NrrChart,
  "customer-acquisition-cost": CacChart,
  "ltv-to-cac-ratio": LtvCacRatioChart,
  "cac-payback-period": CacPaybackChart,
  "revenue-churn-rate": RevenueChurnRateChart,
  // Comparison page — the ArrChart caption already tells the MRR ×12 story.
  "mrr-vs-arr": ArrChart,
  "rule-of-40": RuleOf40Chart,
  "negative-churn": NetRevenueChurnChart,
  // Comparison pages — reuse the chart that already visualises the two sides.
  "nrr-vs-grr": GrossToNetSpreadChart,
  "churn-rate-vs-retention-rate": CustomersRetentionRateChart,
  "arr-per-employee": ArrPerEmployeeChart,
  // Best-practices guides.
  "reduce-saas-churn": ChurnCompoundingChart,
  "how-to-calculate-churn-rate": CustomerChurnRateChart,
  "annual-churn-rate-formula": ChurnCompoundingChart,
  "nrr-from-stripe": NrrChart,
  "good-nrr-benchmarks": NrrBenchmarksChart,
  "average-saas-churn-rate": ChurnBenchmarksChart,
  "stripe-mrr-is-wrong": StripeMrrBridgeChart,
  "trial-to-paid-benchmarks": TrialToPaidBenchmarksChart,
  "activation-rate-benchmarks": ActivationBenchmarksChart,
  // Web-analytics guides.
  "good-bounce-rate": BounceRateBenchmarksChart,
  "traffic-sources-explained": TrafficSourcesChart,
  // Tier-3 glossary pages.
  "annual-contract-value": AcvChart,
  "saas-magic-number": MagicNumberChart,
  "burn-multiple": BurnMultipleChart,
  "renewal-rate": RenewalRateChart,
  "bookings-vs-billings-vs-revenue": DealStagesChart,
  "committed-mrr": CmrrChart,
  "saas-gross-margin": GrossMarginChart,
  "dau-mau-stickiness": DauMauChart,
  // Comparison pages — reuse the chart that already shows both sides.
  "arpa-vs-arpu-vs-asp": ArpaChart,
  "ltv-vs-cac": LtvCacChart,
  "ndr-vs-nrr": NrrChart,
  "acv-vs-arr": ArrChart,
  // In-article section charts (PostSection.chart keys, not post slugs).
  "nrr-whale-test": NrrWhaleTestChart,
  "nrr-grr-composition": NrrGrrCompositionChart,
  "churn-compounding": ChurnCompoundingChart,
  "churn-by-tenure": ChurnByTenureChart,
  "activation-mix-illusion": ActivationMixChart,
  "activation-retention-split": ActivationRetentionSplitChart,
  "expansion-revenue": ExpansionMrrChart,
  "contraction-mrr": ContractionMrrChart,
  "new-mrr": NewMrrChart,
  "churn-mrr": ChurnMrrChart,
  "reactivation-mrr": ReactivationMrrChart,
  "mrr-movements": MrrMovementsChart,
  "grr-rate": GrrChart,
  mrr: MrrChart,
  "net-new-revenue": NetNewRevenueChart,
  "mrr-growth-rate": MrrGrowthRateChart,
  arr: ArrChart,
  "paying-accounts": PayingAccountsChart,
  "paying-customers-movements": PayingCustomerMovementsChart,
  asp: AverageSalesPriceChart,
  "avg-expansion": AverageExpansionAmountChart,
  "avg-contraction": AverageContractionAmountChart,
  "avg-churn": AverageChurnAmountChart,
  "avg-reactivation": AverageReactivationAmountChart,
  "customers-retention-rate": CustomersRetentionRateChart,
  "realized-customers-lifetime-value": RealizedClvChart,
  "average-account-lifespan": AverageAccountLifespanChart,
  "average-company-lifespan": AverageCompanyLifespanChart,
  "recovery-rate": WinBackRateChart,
  "median-time-to-return": MedianTimeToReturnChart,
  "trial-to-paid-conversion-rate": TrialToPaidRateChart,
  "expansion-rate": ExpansionRateChart,
  signups: SignupsChart,
  "trials-started": TrialsStartedChart,
  "time-to-activated": TimeToActivatedChart,
  "time-to-engaged": TimeToEngagedChart,
  "conversion-rate": ConversionRateChart,
  "activation-rate": ActivationRateChart,
  "growth-quick-ratio": GrowthQuickRatioChart,
  "gross-churn-coverage": GrossChurnCoverageChart,
  "expansion-vs-contraction-ratio": ExpansionVsContractionRatioChart,
  "expansion-to-contraction-count-ratio": ExpansionToContractionCountRatioChart,
  "growth-efficiency-index": GrowthEfficiencyIndexChart,
  "gross-to-net-spread": GrossToNetSpreadChart,
  arpa: ArpaChart,
  arpu: ArpuChart,
  "active-count": ActiveAccountsChart,
  "engaged-count": EngagedAccountsChart,
  "new-paying-accounts": NewPayingAccountsChart,
  "churned-paying-accounts": ChurnedPayingAccountsChart,
  "churn-acceleration": ChurnAccelerationChart,
  "logo-vs-dollar-churn-gap": LogoVsDollarChurnGapChart,
  "contraction-before-cancellation": ContractionBeforeCancelChart,
  "average-active-users-per-account": AverageActiveUsersChart,
  "accounts-with-single-active-user-mrr-share": SingleUserMrrShareChart,
  "accounts-with-single-active-user-count": SingleUserCountChart,
  "paying-dormant-count": PayingDormantChart,
  "dormant-account-mrr": ZombieRevenueChart,
  "annualized-growth-rate": AnnualizedGrowthRateChart,
  "smoothed-mrr-growth-rate": SmoothedMrrGrowthChart,
  "months-to-double": MonthsToDoubleChart,
  "growth-acceleration": GrowthAccelerationChart,
  "growth-volatility": GrowthVolatilityChart,
  "expansion-velocity": ExpansionVelocityChart,
  "contraction-velocity": ContractionVelocityChart,
  "reactivation-share-of-gross-adds": ReactivationShareChart,
  "annual-share": AnnualShareChart,
  "expansion-saturation": ExpansionSaturationChart,
  "durable-mrr": DurableMrrChart,
  "refund-rate": RefundRateChart,
  "dispute-rate": DisputeRateChart,
  mrr_discount_amount: MrrDiscountAmountChart,
  "new-mrr-discount-value": DiscountValueChart,
  "discounted-first-payment-rate": DiscountedFirstPaymentRateChart,
  "discounted-new-accounts": DiscountedNewAccountsChart,
  "discounted-new-companies": DiscountedNewCompaniesChart,
  "average-new-accounts-discount-size": AvgNewAccountsDiscountChart,
  "average-new-companies-discount-size": AvgNewCompaniesDiscountChart,
  "nrr-rate-cohort": NrrCohortChart,
  "customer-lifetime-value": ClvChart,
  "customer-churn-rate": CustomerChurnRateChart,
};
